import { LocalStoragePersistence } from "@engine/persistence/localStorage.js";
import type { EnginePersistence } from "@engine/persistence/types.js";

const ATTEMPTS_CAP = 100;

export interface MigrationResult {
  migrated: boolean;
  counts: { player: number; mastery: number; attempts: number };
}

/**
 * On first sign-in, copy the active local character's progress into Supabase.
 *
 * Idempotent: bails out if the remote already has a player row (i.e. this
 * user has signed in here before, or signed in on another device).
 *
 * Local data is left intact - signing back out leaves a usable guest shell.
 */
function isValidPlayer(p: unknown): p is { rating: number; gamesPlayed: number } {
  if (!p || typeof p !== "object") return false;
  const rec = p as Record<string, unknown>;
  return (
    typeof rec["rating"] === "number" &&
    Number.isFinite(rec["rating"]) &&
    typeof rec["gamesPlayed"] === "number" &&
    Number.isFinite(rec["gamesPlayed"])
  );
}

function logSkipped(stage: string, err: unknown): void {
  if (typeof console !== "undefined") {
    const detail = err != null && typeof err === "object" ? JSON.stringify(err) : String(err);
    console.error(`[notanyai] migration ${stage} failed; continuing without it:`, detail, err);
  }
}

export async function maybeRunSilentMigration(
  remote: EnginePersistence,
  localPrefix: string
): Promise<MigrationResult> {
  const counts = { player: 0, mastery: 0, attempts: 0 };
  const remotePlayer = await remote.loadPlayer();
  if (remotePlayer) return { migrated: false, counts };

  const local = new LocalStoragePersistence(localPrefix);
  const localPlayer = local.loadPlayer();
  const localMastery = local.loadMastery?.() ?? null;
  const localAttempts = local.loadAttempts(ATTEMPTS_CAP);

  if (!localPlayer && !localMastery && localAttempts.length === 0) {
    return { migrated: false, counts };
  }

  // Each step is best-effort: we want a partial migration to never blank
  // the page. If `savePlayer` throws (RLS, schema drift, transient 5xx),
  // log it, mark this attempt as not-migrated, and move on. A subsequent
  // sign-in or manual save will retry.
  if (isValidPlayer(localPlayer)) {
    try {
      await remote.savePlayer(localPlayer);
      counts.player = 1;
    } catch (err) {
      logSkipped("savePlayer", err);
    }
  } else if (localPlayer) {
    logSkipped("savePlayer", new Error("invalid local player shape"));
  }

  if (localMastery && Object.keys(localMastery.byConcept).length > 0 && remote.saveMastery) {
    try {
      await remote.saveMastery(localMastery);
      counts.mastery = 1;
    } catch (err) {
      logSkipped("saveMastery", err);
    }
  }

  // Send attempts oldest -> newest so remote order matches local chronology.
  const oldestFirst = [...localAttempts].sort((a, b) => (a.at < b.at ? -1 : 1));
  for (const record of oldestFirst) {
    try {
      await remote.appendAttempt(record);
      counts.attempts++;
    } catch (err) {
      logSkipped("appendAttempt", err);
    }
  }

  const migrated = counts.player > 0 || counts.mastery > 0 || counts.attempts > 0;
  return { migrated, counts };
}
